import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { GraphIndex, GraphPath } from '../lib/graph.js';
import {
  groupSourcesForDisplay,
  relationshipProvenanceLabel,
  sourcesForRelationship,
} from '../lib/provenance.js';
import { relationshipLabelForPerson } from '../lib/relationship-view.js';
import { relationshipDateLabel } from '../lib/relationship-time.js';
import {
  createStoryPlaybackState,
  currentStoryPlaybackStep,
  storyPathKey,
  storyPlaybackReducer,
} from '../lib/story-playback.js';

type StoryPlayerProps = {
  path: GraphPath;
  index: GraphIndex;
  snapshotDate: string;
  featured: boolean;
  yieldKey: string;
  onFocusStep: (sourceId: string, targetId: string) => void;
  onComplete: (path: GraphPath) => void;
};

const STORY_STEP_DURATION_MS = 5_500;

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return reduced;
}

export function StoryPlayer({
  path,
  index,
  snapshotDate,
  featured,
  yieldKey,
  onFocusStep,
  onComplete,
}: StoryPlayerProps) {
  const [state, dispatch] = useReducer(storyPlaybackReducer, path, (initialPath) =>
    createStoryPlaybackState(initialPath, { documentHidden: document.hidden }),
  );
  const reducedMotion = useReducedMotion();
  const lastYieldKey = useRef(yieldKey);
  const completedPathKey = useRef<string | null>(null);
  const callbacks = useRef({ onFocusStep, onComplete });
  callbacks.current = { onFocusStep, onComplete };
  const currentStep = currentStoryPlaybackStep(path, state);
  const currentPathKey = storyPathKey(path);
  const focusedStepIndex = currentStep?.index ?? -1;
  const focusedSourceId = currentStep?.sourceId ?? null;
  const focusedTargetId = currentStep?.targetId ?? null;

  useEffect(() => {
    dispatch({ type: 'path-changed', path });
    completedPathKey.current = null;
  }, [currentPathKey, path]);

  useEffect(() => {
    if (lastYieldKey.current !== yieldKey) {
      lastYieldKey.current = yieldKey;
      dispatch({ type: 'pause' });
    }
  }, [yieldKey]);

  useEffect(() => {
    const onVisibilityChange = () =>
      dispatch({ type: 'visibility-changed', hidden: document.hidden });
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (state.status !== 'playing') {
      return;
    }
    const timeout = globalThis.setTimeout(() => dispatch({ type: 'next' }), STORY_STEP_DURATION_MS);
    return () => globalThis.clearTimeout(timeout);
  }, [state.currentStepIndex, state.status]);

  useEffect(() => {
    if (state.status === 'idle' || focusedSourceId === null || focusedTargetId === null) {
      return;
    }
    callbacks.current.onFocusStep(focusedSourceId, focusedTargetId);
  }, [focusedSourceId, focusedStepIndex, focusedTargetId, state.status]);

  useEffect(() => {
    if (
      state.status === 'completed' &&
      currentPathKey !== null &&
      completedPathKey.current !== currentPathKey
    ) {
      completedPathKey.current = currentPathKey;
      callbacks.current.onComplete(path);
    }
  }, [currentPathKey, path, state.status]);

  const evidence = useMemo(() => {
    if (currentStep === null) {
      return null;
    }
    const connection = index.connectionByRelationshipId.get(currentStep.relationship.id);
    const representative = connection?.representative ?? currentStep.relationship;
    const phases = connection?.phases ?? [currentStep.relationship];
    const sources = [
      ...new Map(
        phases
          .flatMap((phase) => sourcesForRelationship(phase, index))
          .map((source) => [source.id, source]),
      ).values(),
    ];
    return { representative, phases, sources };
  }, [currentStep, index]);

  if (currentStep === null || evidence === null) {
    return null;
  }

  const sourceName = index.peopleById.get(currentStep.sourceId)?.name ?? currentStep.sourceId;
  const targetName = index.peopleById.get(currentStep.targetId)?.name ?? currentStep.targetId;
  const dateLabel = relationshipDateLabel(evidence.representative, snapshotDate);
  const relationshipLabel = relationshipLabelForPerson(
    evidence.representative,
    currentStep.sourceId,
  );
  const sourceGroups = groupSourcesForDisplay(evidence.sources);
  const sourceCount = sourceGroups.reduce((total, group) => total + group.links.length, 0);
  const playbackLabel =
    state.status === 'completed'
      ? 'Story complete'
      : `Step ${currentStep.number} of ${currentStep.total}`;

  return (
    <section
      className="story-player"
      aria-label="Play this source-linked connection story"
      data-reduced-motion={reducedMotion}
    >
      <div className="story-player__heading">
        <div>
          <p className="eyebrow">Play the connection story</p>
          <h3>{playbackLabel}</h3>
          <p className="story-player__cue">
            {featured
              ? 'The first relationship step and its evidence are ready below.'
              : 'Evidence and direct sources are available for every step.'}
          </p>
        </div>
        <div className="story-player__controls" aria-label="Story playback controls">
          {state.status === 'playing' ? (
            <button
              className="button button--small button--gold"
              type="button"
              onClick={() => dispatch({ type: 'pause' })}
            >
              Pause
            </button>
          ) : state.status === 'paused' ? (
            <button
              className="button button--small button--gold"
              type="button"
              onClick={() => dispatch({ type: 'resume' })}
            >
              Resume
            </button>
          ) : state.status === 'completed' ? (
            <button
              className="button button--small button--gold"
              type="button"
              onClick={() => dispatch({ type: 'restart' })}
            >
              Restart
            </button>
          ) : (
            <button
              className="button button--small button--gold"
              type="button"
              onClick={() => dispatch({ type: 'play' })}
            >
              Play
            </button>
          )}
          <button
            className="button button--small"
            type="button"
            disabled={state.status === 'idle' || currentStep.index === 0}
            onClick={() => dispatch({ type: 'previous' })}
          >
            Previous
          </button>
          <button
            className="button button--small"
            type="button"
            disabled={state.status === 'idle' || state.status === 'completed'}
            onClick={() => dispatch({ type: 'next' })}
          >
            {currentStep.index === currentStep.total - 1 ? 'Finish' : 'Next / skip'}
          </button>
        </div>
      </div>

      <div className="story-player__evidence" aria-live="polite" aria-atomic="true">
        <p className="story-player__caption">
          <strong>{sourceName}</strong>
          <span aria-hidden="true"> — </span>
          <span>{relationshipLabel}</span>
          <span aria-hidden="true"> — </span>
          <strong>{targetName}</strong>
        </p>
        <p className="story-player__meta">
          {dateLabel === null ? null : <span>{dateLabel}</span>}
          <strong>{relationshipProvenanceLabel(evidence.representative)}</strong>
          {evidence.phases.length > 1 ? (
            <span>{evidence.phases.length} retained relationship records</span>
          ) : null}
        </p>
        <div className="story-player__evidence-actions">
          <details className="story-player__sources">
            <summary>Evidence ({sourceCount.toLocaleString('en-US')})</summary>
            {sourceCount === 0 ? (
              <em>Source metadata unavailable</em>
            ) : (
              <div className="story-player__source-groups">
                {sourceGroups.map((group) => (
                  <div key={group.label}>
                    <span>{group.label}</span>
                    {group.links.map((link) => (
                      <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer">
                        {link.label}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </details>
          <button
            className="button button--small"
            type="button"
            onClick={() => onFocusStep(currentStep.sourceId, currentStep.targetId)}
          >
            Focus this edge
          </button>
        </div>
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {playbackLabel}: {sourceName}, {relationshipLabel}, {targetName}.
      </p>
    </section>
  );
}
